import type { PlaybookSectionNode } from "@repo/app-types/entities/playbook";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Textarea, TextareaVariant } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import { DiffableField } from "../diffableField.js";

export interface SectionFormProps {
  section: PlaybookSectionNode;
  liveSection?: PlaybookSectionNode;
  onUpdateSection: (updates: Partial<PlaybookSectionNode>) => void;
  isReadOnly?: boolean;
  /**
   * Fill the parent's height, with the description scrolling inside it. For a
   * parent whose height is bounded and doesn't scroll itself.
   */
  fillsHeight?: boolean;
  showsDescriptionHint?: boolean;
}

export const SectionForm: FC<SectionFormProps> = ({
  section,
  liveSection,
  onUpdateSection,
  isReadOnly = false,
  fillsHeight = false,
  showsDescriptionHint = true,
}) => {
  const currentDescription = section.description ?? "";

  return (
    <div
      className={twMerge(
        "space-y-4",
        fillsHeight && "flex min-h-0 flex-1 flex-col",
      )}
    >
      <div>
        <DiffableField
          label={<Label size={Size.SMALL}>Name</Label>}
          savedValue={liveSection?.name}
          value={section.name}
        >
          <Input
            value={section.name}
            onChange={(event) => {
              onUpdateSection({ name: event.target.value });
            }}
            placeholder="Introduction and compliance disclaimers"
            isDisabled={isReadOnly}
            maxLength={300}
          />
        </DiffableField>
      </div>

      <div className={fillsHeight ? "flex min-h-0 flex-1 flex-col" : undefined}>
        <DiffableField
          label={<Label size={Size.SMALL}>Description</Label>}
          savedValue={liveSection ? (liveSection.description ?? "") : undefined}
          value={currentDescription}
          diffClassName={
            fillsHeight ? "min-h-0 flex-1 overflow-y-auto" : undefined
          }
        >
          <Textarea
            value={currentDescription}
            onChange={(newValue) => {
              onUpdateSection({ description: newValue });
            }}
            placeholder="Describe this section..."
            variant={TextareaVariant.DEFAULT}
            isDisabled={isReadOnly}
            maxLength={2000}
            fillHeight={fillsHeight}
          />
        </DiffableField>
        {showsDescriptionHint ? (
          <p className="mt-2 shrink-0 text-sm text-gray-600">
            (Optional) This description helps the AI grader understand when to
            grade or skip this section.
          </p>
        ) : null}
      </div>
    </div>
  );
};
